import { Fragment, useState, type FormEvent } from "react";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import type { CarrierQuestion, QuoteAnswer } from "@/lib/brokerSubmissions";
import { messageFor } from "@/lib/errors";

// BRK-S054 — the generic carrier question form. Renders each NORMALIZED
// question type (PMS-S145): boolean → a Yes/No select, select → the carrier's
// options, number → a numeric input, text → a text field. Consecutive
// questions sharing a `group` render under one group heading. Required
// questions are validated before submit with a handled inline message; answers
// are emitted as the wire shape [{questionId, answer, questionText}] with
// unanswered optionals omitted.
export function QuestionForm({
  questions,
  onSubmit,
  submitLabel = "Request bindable quote",
  pendingLabel = "Requesting…",
}: {
  questions: CarrierQuestion[];
  /** Send the answers; throwing surfaces the message inline (NN #7). */
  onSubmit: (answers: QuoteAnswer[]) => Promise<void>;
  submitLabel?: string;
  pendingLabel?: string;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setValue(id: string, value: string) {
    setValues((v) => ({ ...v, [id]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const missing = questions.find(
      (q) => q.required && !(values[q.id] ?? "").trim(),
    );
    if (missing) {
      setError(`Answer “${missing.prompt}” to continue.`);
      return;
    }
    const answers: QuoteAnswer[] = questions
      .filter((q) => (values[q.id] ?? "").trim())
      .map((q) => ({
        questionId: q.id,
        answer: values[q.id].trim(),
        questionText: q.prompt,
      }));
    setPending(true);
    setError(null);
    try {
      await onSubmit(answers);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-3 pt-1"
      noValidate
      onSubmit={handleSubmit}
    >
      {error && (
        <p role="alert" className="text-sm font-normal text-brk-danger">
          {error}
        </p>
      )}
      {questions.map((q, i) => (
        <Fragment key={q.id}>
          {q.group && q.group !== questions[i - 1]?.group && (
            <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-brk-dim">
              {q.group}
            </p>
          )}
          <QuestionControl
            question={q}
            value={values[q.id] ?? ""}
            onChange={(v) => setValue(q.id, v)}
          />
        </Fragment>
      ))}
      <button
        type="submit"
        disabled={pending}
        className="self-start rounded-md bg-brk-accent px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
      >
        {pending ? pendingLabel : submitLabel}
      </button>
    </form>
  );
}

function QuestionControl({
  question,
  value,
  onChange,
}: {
  question: CarrierQuestion;
  value: string;
  onChange: (value: string) => void;
}) {
  switch (question.type) {
    case "boolean":
      return (
        <SelectField
          label={question.prompt}
          placeholder="Select…"
          options={[
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ]}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
        />
      );
    case "select":
      return (
        <SelectField
          label={question.prompt}
          placeholder="Select…"
          options={(question.options ?? []).map((o) => ({
            value: o,
            label: o,
          }))}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
        />
      );
    case "number":
      return (
        <TextField
          label={question.prompt}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
        />
      );
    case "text":
      return (
        <TextField
          label={question.prompt}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={question.required}
        />
      );
  }
}
